// Gating de acesso granular por RECURSO para o painel da averbadora.
//
// Modelo atual (2026-07-14): cada usuario tem `permissoes: string[]` — um array
// livre de "resource keys" (ex.: "bancos", "adf", "prefeituras"). "*" = wildcard
// (supervisor). O JWT carrega esse array em `averbadora_permissoes`.
//
// Presets sao apenas atalhos de preenchimento no modal de criar/editar usuario.
// Depois de escolher um preset, o admin pode marcar/desmarcar caixas — vira
// "personalizado". A fonte de verdade e sempre o array `permissoes`.
//
// Enforcement REAL vive no backend (requirePermissao). Esta lib faz o gate no
// frontend: esconde itens de menu, redireciona rota fora do escopo.

export type AverbadoraPerfil =
  | "operador"
  | "supervisor"
  | "comercial"
  | "financeiro"
  | "auditoria"
  | "personalizado";

/** Todos os perfis existentes — util pra iterar em UIs. */
export const TODOS_PERFIS: AverbadoraPerfil[] = [
  "supervisor",
  "operador",
  "comercial",
  "financeiro",
  "auditoria",
  "personalizado",
];

/** Todos os recursos (resource keys) do painel, agrupados por categoria.
 *  Espelha os itens do menu em layout.tsx — se voce adicionar aba nova, adicione
 *  a chave aqui pra ela aparecer na matriz de checkboxes ao criar usuario. */
export const RESOURCE_GROUPS: {
  titulo: string;
  recursos: { key: string; label: string; descricao?: string }[];
}[] = [
  {
    titulo: "Geral",
    recursos: [
      { key: "dashboard", label: "Dashboard", descricao: "Visao geral, KPIs" },
      { key: "health", label: "Health", descricao: "Uptime/p95 dos endpoints" },
      { key: "conta", label: "Minha conta", descricao: "Self-service (senha, 2FA, tema)" },
    ],
  },
  {
    titulo: "Cadastros operacionais",
    recursos: [
      // A chave gravada continua `prefeituras` (feature 009): trocá-la revogaria
      // a permissão de todo perfil existente. O rótulo acompanha a tela.
      { key: "prefeituras", label: "Órgãos e entidades" },
      { key: "convenios", label: "Convenios" },
      { key: "contratos", label: "Contratos" },
      { key: "servidores", label: "Servidores" },
      { key: "pre-reservas", label: "Pre-reservas" },
      { key: "adf", label: "ADF (Descontos em folha)" },
      { key: "portabilidade", label: "Portabilidade (marketplace)" },
      { key: "beneficios", label: "Beneficios" },
      { key: "telemedicina", label: "Telemedicina" },
      { key: "interessados", label: "Interessados" },
    ],
  },
  {
    titulo: "Consignatarias",
    recursos: [{ key: "bancos", label: "Consignatarias", descricao: "CRUD sensivel" }],
  },
  {
    titulo: "Financeiro",
    recursos: [
      { key: "folhas", label: "Folhas" },
      { key: "conciliacao", label: "Conciliação de descontos" },
      { key: "troca-arquivos", label: "Troca de arquivos" },
      {
        key: "layouts",
        label: "Layouts de arquivo",
        descricao: "Especificacao dos arquivos do ciclo (so leitura)",
      },
      { key: "bate-carteira", label: "Bate de carteira" },
      {
        key: "rubricas",
        label: "Catálogo de rubricas",
        descricao: "Classifica as verbas da folha; verba não confirmada não gera dívida",
      },
    ],
  },
  {
    titulo: "Comercial",
    recursos: [
      { key: "vitrine", label: "Vitrine" },
      { key: "comunicados", label: "Comunicados (todos)" },
      { key: "comunicados-banco", label: "Comunicados — Consignataria" },
      { key: "comunicados-servidor", label: "Comunicados — Servidor" },
      { key: "comunicados-prefeitura", label: "Comunicados — Prefeitura" },
    ],
  },
  {
    titulo: "E-mails do sistema",
    recursos: [
      { key: "email-sistema", label: "Editor de templates" },
      { key: "email-primeiro-acesso", label: "E-mail: primeiro acesso" },
      { key: "email-recuperar-senha", label: "E-mail: recuperar senha" },
      { key: "email-redefinir-senha", label: "E-mail: redefinir senha" },
      { key: "email-simulacao", label: "E-mail: simulacao" },
      { key: "email-beneficios", label: "E-mail: beneficios" },
      { key: "email-pedidos-cancelamento", label: "E-mail: pedidos de cancelamento" },
      { key: "email-arquivo-pendente", label: "E-mail: arquivo mensal pendente" },
      { key: "termos", label: "Termos (editor)" },
    ],
  },
  {
    titulo: "Auditoria",
    recursos: [
      { key: "auditoria", label: "Trilha de auditoria" },
      { key: "logs", label: "Logs" },
    ],
  },
  {
    titulo: "Administracao restrita",
    recursos: [
      { key: "perfis", label: "Usuarios e permissoes" },
      { key: "id-unico", label: "ID unico" },
      { key: "api", label: "API (menu)" },
      { key: "api-docs", label: "API — Documentacao" },
      { key: "api-tokens", label: "API — Tokens" },
      { key: "api-uso", label: "API — Uso de API", descricao: "Chamadas da API externa da consignataria. Nao e o faturamento de comissao." },
      { key: "api-webhooks", label: "API — Webhooks" },
      { key: "configuracoes", label: "Configuracoes", descricao: "SMTP + OpenAI (troca de chaves)" },
      {
        key: "suporte",
        label: "Suporte",
        descricao: "Edita email/WhatsApp/horario exibidos ao servidor em Conta > Suporte",
      },
      {
        key: "manutencao",
        label: "Manutencao (destrutivo)",
        descricao: "Purge de contratos, reset de contas, reseed do banco. So supervisor por padrao.",
      },
    ],
  },
];

/** Todos os keys (flat) — util pra checkbox "marcar tudo". */
export const TODAS_PERMISSOES: string[] = RESOURCE_GROUPS.flatMap((g) => g.recursos.map((r) => r.key));

/** Presets de fabrica — pre-preenchem a matriz. Alinhado com PRESETS do backend
 *  (apps/api/src/modules/admin/perfis-admin.ts). */
export const PRESETS: Record<AverbadoraPerfil, string[]> = {
  supervisor: ["*"],
  operador: [
    "dashboard",
    "health",
    "conta",
    "prefeituras",
    "convenios",
    "contratos",
    "servidores",
    "pre-reservas",
    "adf",
    "beneficios",
    "telemedicina",
    "interessados",
    "layouts",
    "conciliacao",
  ],
  comercial: [
    "dashboard",
    "health",
    "conta",
    "vitrine",
    "comunicados",
    "comunicados-banco",
    "comunicados-servidor",
    "comunicados-prefeitura",
    "interessados",
  ],
  financeiro: ["dashboard", "health", "conta", "folhas", "troca-arquivos", "bate-carteira", "adf", "layouts", "conciliacao"],
  auditoria: ["dashboard", "health", "conta", "auditoria", "logs"],
  personalizado: [],
};

/** True se o conjunto de permissoes casa exatamente com algum preset (mesmo set). */
export function detectarPreset(permissoes: string[]): AverbadoraPerfil {
  const set = new Set(permissoes);
  for (const [nome, keys] of Object.entries(PRESETS) as [AverbadoraPerfil, string[]][]) {
    if (nome === "personalizado") continue;
    if (keys.length !== set.size) continue;
    if (keys.every((k) => set.has(k))) return nome;
  }
  return "personalizado";
}

/** Autoriza acesso a `key` com base nas `permissoes` do usuario.
 *  - `null`/`undefined` (dev-user ou JWT antigo) = passa em tudo (retrocompat).
 *  - `"*"` (wildcard) = passa em tudo.
 *  - Exact match = passa.
 *  Chaves nao mapeadas em RESOURCE_GROUPS ainda funcionam — sao apenas nao
 *  aparecem na matriz de checkboxes por padrao. */
/**
 * Chaves que passaram a valer por outra, quando um recurso mudou de nome.
 *
 * `organizacoes` aceita quem tem `prefeituras`: a tela virou "Órgãos e entidades"
 * na feature 009, e a chave do menu mudou junto. Sem este mapa, TODO perfil já
 * gravado — que tem `prefeituras` — perderia o acesso ao cadastro no instante do
 * deploy, e seria redirecionado ao dashboard sem explicação.
 *
 * É o mesmo mecanismo que o portal usa para `folhas → troca-arquivos`, e existe
 * pela mesma razão: renomear um recurso não pode revogar permissão de ninguém em
 * silêncio. A alternativa seria migrar as permissões gravadas de todos os
 * perfis, que é irreversível e mais arriscado do que aceitar um alias.
 */
const ALIAS_PERMISSAO: Record<string, readonly string[]> = {
  organizacoes: ["prefeituras"],
  // A tela de relações institucionais (feature 009, US2) é o outro lado do
  // mesmo cadastro, e a API a protege com a MESMA chave (`requirePermissao(j,
  // "prefeituras")`). Um item de menu que abre e responde 403 seria pior do que
  // não ter o item.
  relacoes: ["prefeituras"],
  // Uso de API nasceu depois dos perfis gravados. Quem já opera tokens ou o
  // menu de API vê o relatório sem uma caixa nova obrigatória. A chave própria
  // existe para conceder o relatório sem conceder emissão de token.
  "api-uso": ["api-tokens", "api"],
  // A lista de refinanciamentos e' leitura da carteira: a API a guarda com
  // `contratos` (GET /v1/admin/refinanciamentos).
  refinanciamentos: ["contratos"],
  // O arquivo mensal da consignatária (#198): a API guarda upload, prévia e
  // confirmação com `prefeituras` (decidido em 25/09/2026).
  consignatarias: ["prefeituras"],
  // Os pedidos de cancelamento do servidor (#276): a API guarda a fila e a
  // resposta com `servidores` — e' um pedido do servidor sobre o que e' dele.
  "solicitacoes-cancelamento": ["servidores"],
};

export function podeAcessar(permissoes: string[] | null | undefined, key: string): boolean {
  if (!permissoes || permissoes.length === 0) return true; // dev-user sem claim
  if (permissoes.includes("*")) return true;
  if (permissoes.includes(key)) return true;
  return (ALIAS_PERMISSAO[key] ?? []).some((a) => permissoes.includes(a));
}

/** Super admin = quem tem wildcard "*" (ou JWT sem claim = dev-user).
 *  Espelha requireSuperAdmin do backend — usado pra esconder acoes exclusivas
 *  (ex.: impersonate servidor) de operadores comuns com acesso ao recurso. */
export function isSuperAdmin(permissoes: string[] | null | undefined): boolean {
  if (!permissoes || permissoes.length === 0) return true;
  return permissoes.includes("*");
}

/** Le o array de permissoes do JWT (claim `averbadora_permissoes`).
 *  Fallback: se so tem `averbadora_perfil` (JWT antigo), deriva do preset. */
export function readAverbadoraPermissoesFromJwt(accessToken: string | null | undefined): string[] | null {
  if (!accessToken) return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (Array.isArray(payload.averbadora_permissoes)) {
      return payload.averbadora_permissoes as string[];
    }
    // Retrocompat: JWT antigo so tem `averbadora_perfil`.
    const p = payload.averbadora_perfil as AverbadoraPerfil | undefined;
    if (p && PRESETS[p]) return [...PRESETS[p]];
    return null;
  } catch {
    return null;
  }
}

/** Retrocompat com codigo que ainda le o "perfil" (label). */
export function readAverbadoraPerfilFromJwt(accessToken: string | null | undefined): AverbadoraPerfil | null {
  if (!accessToken) return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const p = payload.averbadora_perfil;
    if (TODOS_PERFIS.includes(p)) return p as AverbadoraPerfil;
    return null;
  } catch {
    return null;
  }
}
